import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  getEmployeeLeaveSummary,
  getMonthlyLeaveSummary,
  getLeaveTransactions,
} from "../../api/api";

// =====================================================
// GLOBAL STYLES
// =====================================================

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

:root {
  --bg: #f0f4f8;
  --surface: #ffffff;
  --surface2: #f7f9fc;
  --border: #e2e8f0;
  --border2: #cbd5e1;

  --accent: #2563eb;
  --accent-lt: #eff6ff;
  --green: #16a34a;
  --green-lt: #f0fdf4;
  --red: #dc2626;
  --red-lt: #fef2f2;
  --amber: #d97706;
  --amber-lt: #fffbeb;
  --purple: #7c3aed;
  --purple-lt: #f5f3ff;

  --text: #0f172a;
  --text2: #475569;
  --muted: #94a3b8;

  --font: 'Plus Jakarta Sans', sans-serif;
  --mono: 'JetBrains Mono', monospace;

  --radius: 14px;
  --radius-sm: 9px;
  --shadow: 0 1px 4px rgba(15,23,42,0.06), 0 4px 16px rgba(15,23,42,0.06);
  --shadow-sm: 0 1px 3px rgba(15,23,42,0.08);
  --drawer-w: 480px;
}

.ld-page {
  font-family: var(--font);
  background: var(--bg);
  min-height: 100vh;
  color: var(--text);
  padding: 28px 24px 72px;
  transition: padding-right 0.3s ease;
}

.ld-page.drawer-open {
  padding-right: calc(var(--drawer-w) + 24px);
}

@media (max-width: 860px) {
  .ld-page.drawer-open {
    padding-right: 24px;
  }
}

/* HEADER */
.ld-header {
  margin-bottom: 28px;
}

.ld-title {
  font-size: clamp(20px, 4vw, 30px);
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.6px;
  line-height: 1.15;
}

.ld-title span {
  color: var(--accent);
}

.ld-subtitle {
  margin-top: 5px;
  color: var(--muted);
  font-size: 13.5px;
  font-weight: 500;
}

/* SEARCH */
.ld-search-wrap {
  margin-bottom: 18px;
  position: relative;
}

.ld-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  pointer-events: none;
}

.ld-search {
  width: 100%;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-family: var(--font);
  font-size: 14px;
  padding: 11px 16px 11px 42px;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
  box-shadow: var(--shadow-sm);
}

.ld-search:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
}

.ld-search::placeholder {
  color: var(--muted);
}

/* CARD */
.ld-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 22px 20px;
  margin-bottom: 18px;
  box-shadow: var(--shadow);
}

.ld-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.ld-card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
}

.ld-card-count {
  background: var(--accent-lt);
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 99px;
}

/* TABLE */
.ld-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ld-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;
  font-size: 13.5px;
}

.ld-table th {
  text-align: left;
  padding: 9px 14px;
  background: var(--surface2);
  color: var(--muted);
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  white-space: nowrap;
  border-bottom: 1px solid var(--border);
}

.ld-table th:first-child {
  border-radius: 8px 0 0 8px;
}

.ld-table th:last-child {
  border-radius: 0 8px 8px 0;
}

.ld-table td {
  padding: 13px 14px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
  color: var(--text);
  line-height: 1.5;
}

.ld-table tr:last-child td {
  border-bottom: none;
}

.ld-table tbody tr.clickable {
  cursor: pointer;
  transition: background 0.12s;
}

.ld-table tbody tr.clickable:hover td {
  background: #f8faff;
}

.ld-table tbody tr.clickable.active td {
  background: var(--accent-lt);
}

.emp-name {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.emp-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: var(--accent);
  flex-shrink: 0;
  border: 1.5px solid #bfdbfe;
}

.num {
  font-family: var(--mono);
  font-size: 13px;
  font-weight: 500;
}

.c-green {
  color: var(--green);
}

.c-red {
  color: var(--red);
}

.c-amber {
  color: var(--amber);
}

.c-blue {
  color: var(--accent);
}

.c-purple {
  color: var(--purple);
}

.c-muted {
  color: var(--muted);
}

/* BADGE */
.ld-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 99px;
  font-size: 11.5px;
  font-weight: 700;
  font-family: var(--font);
  white-space: nowrap;
}

.ld-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.badge-credit {
  background: var(--green-lt);
  color: var(--green);
}

.badge-debit {
  background: var(--red-lt);
  color: var(--red);
}

.badge-rollback {
  background: var(--purple-lt);
  color: var(--purple);
}

.badge-default {
  background: var(--surface2);
  color: var(--text2);
}

/* LOADING / EMPTY */
.ld-loading {
  padding: 44px;
  text-align: center;
  color: var(--muted);
  font-size: 14px;
}

.ld-spinner {
  width: 26px;
  height: 26px;
  border: 3px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.65s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.ld-empty {
  padding: 44px;
  text-align: center;
  color: var(--muted);
  font-size: 14px;
}

/* MOBILE EMPLOYEE CARDS */
.emp-mobile-list {
  display: none;
}

@media (max-width: 600px) {
  .emp-desktop-table {
    display: none;
  }

  .emp-mobile-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

.emp-mobile-card {
  background: var(--surface2);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.emp-mobile-card:hover {
  border-color: var(--accent);
}

.emp-mobile-card.active {
  border-color: var(--accent);
  background: var(--accent-lt);
}

.emp-mobile-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.emp-mobile-name {
  font-weight: 700;
  font-size: 14.5px;
  flex: 1;
}

.emp-mobile-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.emp-ms-label {
  font-size: 10px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.emp-ms-val {
  font-family: var(--mono);
  font-size: 15px;
  font-weight: 600;
  margin-top: 2px;
}

/* DRAWER */
.ld-drawer-overlay {
  display: none;
}

@media (max-width: 860px) {
  .ld-drawer-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,0.35);
    z-index: 199;
    animation: fadeIn 0.2s ease;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.ld-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: var(--drawer-w);
  background: var(--surface);
  border-left: 1px solid var(--border2);
  box-shadow: -4px 0 24px rgba(15,23,42,0.1);
  z-index: 200;
  display: flex;
  flex-direction: column;
  animation: drawerSlideIn 0.25s cubic-bezier(0.4,0,0.2,1);
  overflow: hidden;
}

@keyframes drawerSlideIn {
  from {
    transform: translateX(100%);
    opacity: 0.6;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 860px) {
  .ld-drawer {
    top: auto;
    width: 100%;
    max-height: 88vh;
    border-left: none;
    border-top: 1px solid var(--border2);
    border-radius: 18px 18px 0 0;
    box-shadow: 0 -4px 24px rgba(15,23,42,0.12);
    animation: sheetSlideUp 0.28s cubic-bezier(0.4,0,0.2,1);
  }

  @keyframes sheetSlideUp {
    from {
      transform: translateY(100%);
    }

    to {
      transform: translateY(0);
    }
  }
}

.ld-drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.ld-drawer-handle {
  display: none;
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: var(--border2);
  margin: 10px auto 0;
  flex-shrink: 0;
}

@media (max-width: 860px) {
  .ld-drawer-handle {
    display: block;
  }
}

.ld-drawer-title {
  flex: 1;
}

.ld-drawer-name {
  font-size: 15px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.2;
}

.ld-drawer-meta {
  font-size: 12px;
  color: var(--muted);
  margin-top: 3px;
}

.ld-drawer-meta strong {
  color: var(--accent);
}

.ld-drawer-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--surface2);
  border: 1px solid var(--border2);
  color: var(--text2);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  font-family: var(--font);
}

.ld-drawer-close:hover {
  background: var(--red-lt);
  border-color: var(--red);
  color: var(--red);
}

.ld-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  -webkit-overflow-scrolling: touch;
}

/* TABS */
.ld-tabs {
  display: flex;
  gap: 4px;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 4px;
  margin-bottom: 18px;
}

.ld-tab {
  flex: 1;
  padding: 8px 12px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: var(--muted);
  font-family: var(--font);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.ld-tab.active {
  background: var(--surface);
  color: var(--accent);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
}

/* MONTH PILL GRID */
.month-pill-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.month-pill {
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 14px 16px;
  transition: box-shadow 0.15s;
}

.month-pill:hover {
  box-shadow: var(--shadow);
}

.month-pill-name {
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: var(--accent);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
}

.month-pill-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12.5px;
  margin-bottom: 5px;
}

.month-pill-row:last-child {
  margin-bottom: 0;
}

.month-pill-row .mp-label {
  color: var(--text2);
  font-weight: 500;
}

.month-pill-row .mp-val {
  font-family: var(--mono);
  font-weight: 600;
  font-size: 13px;
}

/* TRANSACTION DETAIL CELLS */
.txn-main {
  font-weight: 600;
  font-size: 13.5px;
  color: var(--text);
}

.txn-sub {
  font-size: 12px;
  color: var(--text2);
  margin-top: 2px;
}

.txn-chip {
  display: inline-block;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 1px 6px;
  font-size: 11px;
  font-family: var(--mono);
  font-weight: 500;
  color: var(--text2);
  margin-top: 3px;
}

.detail-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
}

.detail-label {
  color: var(--muted);
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 58px;
}

.detail-val {
  font-family: var(--mono);
  font-weight: 600;
  font-size: 13px;
  color: var(--text);
}

.bal-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.bal-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
}

.bal-label {
  color: var(--muted);
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 36px;
}

.bal-val {
  font-family: var(--mono);
  font-weight: 600;
  font-size: 13px;
}

.bal-diff {
  font-family: var(--mono);
  font-weight: 700;
  font-size: 12px;
}

.remarks-cell {
  font-size: 12px;
  color: var(--text2);
  max-width: 180px;
  line-height: 1.5;
}

/* MOBILE TXN CARDS */
.txn-mobile-list {
  display: none;
}

@media (max-width: 600px) {
  .txn-desktop-table {
    display: none;
  }

  .txn-mobile-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

.txn-mobile-card {
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 14px 16px;
}

.txn-mobile-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}

.txn-mobile-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
  font-size: 12px;
  color: var(--text2);
  line-height: 1.5;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .ld-page {
    padding: 16px 14px 52px;
  }

  .ld-card {
    padding: 16px 14px;
  }
}
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById("ld-styles-v5")
) {
  const tag = document.createElement("style");
  tag.id = "ld-styles-v5";
  tag.textContent = GLOBAL_CSS;
  document.head.appendChild(tag);
}

// =====================================================
// HELPERS
// =====================================================

const fmt = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0.0";
  }

  return number.toFixed(1);
};

const initials = (name = "") => {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return "?";
  }

  return parts
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

function getBadgeClass(type = "") {
  const transactionType = String(type).toLowerCase();

  if (transactionType.includes("rollback")) {
    return "badge-rollback";
  }

  if (transactionType.includes("credit")) {
    return "badge-credit";
  }

  if (
    transactionType.includes("approved") ||
    transactionType.includes("debit")
  ) {
    return "badge-debit";
  }

  return "badge-default";
}

// UTC → IST (+5:30)
function toIST(utcString) {
  if (!utcString) {
    return new Date(NaN);
  }

  const date = new Date(utcString);

  if (Number.isNaN(date.getTime())) {
    return new Date(NaN);
  }

  return new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function monthLabel(month) {
  if (month == null || month === "") {
    return "—";
  }

  const value = String(month).trim();

  const isoShort = value.match(/^(\d{4})-(\d{1,2})$/);

  if (isoShort) {
    const monthNumber = parseInt(isoShort[2], 10);

    if (monthNumber >= 1 && monthNumber <= 12) {
      return `${MONTH_SHORT[monthNumber - 1]} '${isoShort[1].slice(2)}`;
    }
  }

  const isoFull = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoFull) {
    const monthNumber = parseInt(isoFull[2], 10);

    if (monthNumber >= 1 && monthNumber <= 12) {
      return `${MONTH_SHORT[monthNumber - 1]} '${isoFull[1].slice(2)}`;
    }
  }

  if (/[a-zA-Z]/.test(value)) {
    return value;
  }

  if (/^\d+$/.test(value)) {
    const date = new Date(Number(value));

    if (!Number.isNaN(date.getTime())) {
      return `${MONTH_SHORT[date.getMonth()]} '${String(
        date.getFullYear()
      ).slice(2)}`;
    }
  }

  const date = new Date(value);

  if (!Number.isNaN(date.getTime())) {
    return `${MONTH_SHORT[date.getMonth()]} '${String(
      date.getFullYear()
    ).slice(2)}`;
  }

  return value;
}

function monthFull(month) {
  if (month == null || month === "") {
    return "—";
  }

  const value = String(month).trim();

  const isoShort = value.match(/^(\d{4})-(\d{1,2})$/);

  if (isoShort) {
    const monthNumber = parseInt(isoShort[2], 10);

    if (monthNumber >= 1 && monthNumber <= 12) {
      return `${MONTH_NAMES[monthNumber - 1]} ${isoShort[1]}`;
    }
  }

  if (/[a-zA-Z]/.test(value)) {
    return value;
  }

  const date = new Date(/^\d+$/.test(value) ? Number(value) : value);

  if (!Number.isNaN(date.getTime())) {
    return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  }

  return value;
}

// =====================================================
// MAIN
// =====================================================

export default function LeaveDashboard() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("monthly");

  useEffect(() => {
    const handler = (event) => {
      if (event.key === "Escape") {
        setSelectedEmployee(null);
      }
    };

    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, []);

  useEffect(() => {
    fetchEmployeeSummary();
  }, []);

  const fetchEmployeeSummary = async () => {
    try {
      setLoading(true);

      const data = await getEmployeeLeaveSummary();

      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[LeaveDashboard] Failed to fetch employee summary:", error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const openEmployee = useCallback(async (employee) => {
    if (!employee?.user_id) {
      return;
    }

    try {
      setSelectedEmployee(employee);
      setActiveTab("monthly");
      setTransactionLoading(true);

      const currentYear = new Date().getFullYear();

      const [monthly, txns] = await Promise.all([
        getMonthlyLeaveSummary(employee.user_id, currentYear),
        getLeaveTransactions(employee.user_id),
      ]);

      setMonthlySummary(Array.isArray(monthly) ? monthly : []);
      setTransactions(Array.isArray(txns) ? txns : []);
    } catch (error) {
      console.error(
        "[LeaveDashboard] Failed to fetch employee details:",
        error
      );

      setMonthlySummary([]);
      setTransactions([]);
    } finally {
      setTransactionLoading(false);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    setSelectedEmployee(null);
  }, []);

  const handleRowClick = useCallback(
    (employee) => {
      if (selectedEmployee?.user_id === employee?.user_id) {
        closeDrawer();
      } else {
        openEmployee(employee);
      }
    },
    [selectedEmployee, openEmployee, closeDrawer]
  );

  const filteredEmployees = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    if (!searchTerm) {
      return employees;
    }

    return employees.filter((employee) => {
      const name = String(employee?.user_name || "").toLowerCase();
      const email = String(employee?.email || "").toLowerCase();
      const empCode = String(employee?.emp_code || "").toLowerCase();

      return (
        name.includes(searchTerm) ||
        email.includes(searchTerm) ||
        empCode.includes(searchTerm)
      );
    });
  }, [employees, search]);

  const drawerOpen = Boolean(selectedEmployee);

  return (
    <div className={`ld-page${drawerOpen ? " drawer-open" : ""}`}>
      {/* HEADER */}
      <div className="ld-header">
        <div className="ld-title">
          Leave <span>Dashboard</span>
        </div>

        <div className="ld-subtitle">
          All employees · leave analytics &amp; transactions
        </div>
      </div>

      {/* SEARCH */}
      <div className="ld-search-wrap">
        <span className="ld-search-icon">
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </span>

        <input
          className="ld-search"
          type="text"
          placeholder="Search employee by name, email or employee code…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {/* EMPLOYEE LIST */}
      <div className="ld-card">
        <div className="ld-card-header">
          <div className="ld-card-title">
            All Employees
            {!loading && (
              <span className="ld-card-count">
                {filteredEmployees.length}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : filteredEmployees.length === 0 ? (
          <div className="ld-empty">
            No employees match your search.
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE */}
            <div className="ld-table-wrap emp-desktop-table">
              <table className="ld-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Total Balance</th>
                    <th>Planned (PL)</th>
                    <th>Emergency (EL)</th>
                    <th>Used This Month</th>
                    <th>Earned This Month</th>
                    <th>LOP This Month</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEmployees.map((employee) => {
                    const isActive =
                      selectedEmployee?.user_id === employee?.user_id;

                    return (
                      <tr
                        key={employee.user_id}
                        className={`clickable${isActive ? " active" : ""}`}
                        onClick={() => handleRowClick(employee)}
                      >
                        <td>
                          <div className="emp-name">
                            <div className="emp-avatar">
                              {initials(employee.user_name)}
                            </div>

                            {employee.user_name || "Employee"}
                          </div>
                        </td>

                        <td>
                          <span className="num c-blue">
                            {fmt(employee.total_balance)}
                          </span>
                        </td>

                        <td>
                          <span className="num">
                            {fmt(employee.planned_balance)}
                          </span>
                        </td>

                        <td>
                          <span className="num c-purple">
                            {fmt(employee.unplanned_balance)}
                          </span>
                        </td>

                        <td>
                          <span className="num c-red">
                            {fmt(employee.debited_this_month)}
                          </span>
                        </td>

                        <td>
                          <span className="num c-green">
                            {fmt(employee.credited_this_month)}
                          </span>
                        </td>

                        <td>
                          <span className="num c-amber">
                            {fmt(employee.loss_of_pay_this_month)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE CARDS */}
            <div className="emp-mobile-list">
              {filteredEmployees.map((employee) => {
                const isActive =
                  selectedEmployee?.user_id === employee?.user_id;

                return (
                  <div
                    key={employee.user_id}
                    className={`emp-mobile-card${
                      isActive ? " active" : ""
                    }`}
                    onClick={() => handleRowClick(employee)}
                  >
                    <div className="emp-mobile-top">
                      <div className="emp-avatar">
                        {initials(employee.user_name)}
                      </div>

                      <div className="emp-mobile-name">
                        {employee.user_name || "Employee"}
                      </div>
                    </div>

                    <div className="emp-mobile-grid">
                      <MobileStat
                        label="Total"
                        value={fmt(employee.total_balance)}
                        color="var(--accent)"
                      />

                      <MobileStat
                        label="PL"
                        value={fmt(employee.planned_balance)}
                      />

                      <MobileStat
                        label="EL"
                        value={fmt(employee.unplanned_balance)}
                        color="var(--purple)"
                      />

                      <MobileStat
                        label="Used"
                        value={fmt(employee.debited_this_month)}
                        color="var(--red)"
                      />

                      <MobileStat
                        label="Earned"
                        value={fmt(employee.credited_this_month)}
                        color="var(--green)"
                      />

                      <MobileStat
                        label="LOP"
                        value={fmt(employee.loss_of_pay_this_month)}
                        color="var(--amber)"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* DRAWER */}
      {drawerOpen && (
        <>
          <div
            className="ld-drawer-overlay"
            onClick={closeDrawer}
            aria-hidden="true"
          />

          <div
            className="ld-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Employee leave details"
          >
            <div className="ld-drawer-handle" />

            {/* DRAWER HEADER */}
            <div className="ld-drawer-header">
              <div
                className="emp-avatar"
                style={{
                  width: 42,
                  height: 42,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {initials(selectedEmployee.user_name)}
              </div>

              <div className="ld-drawer-title">
                <div className="ld-drawer-name">
                  {selectedEmployee.user_name || "Employee"}
                </div>

                <div className="ld-drawer-meta">
                  Balance:&nbsp;
                  <strong>
                    {fmt(selectedEmployee.total_balance)}
                  </strong>{" "}
                  days
                  &nbsp;·&nbsp;PL:&nbsp;
                  <strong style={{ color: "var(--text)" }}>
                    {fmt(selectedEmployee.planned_balance)}
                  </strong>
                  &nbsp;·&nbsp;EL:&nbsp;
                  <strong style={{ color: "var(--purple)" }}>
                    {fmt(selectedEmployee.unplanned_balance)}
                  </strong>
                </div>
              </div>

              <button
                type="button"
                className="ld-drawer-close"
                onClick={closeDrawer}
                aria-label="Close drawer"
              >
                ✕
              </button>
            </div>

            {/* DRAWER BODY */}
            <div className="ld-drawer-body">
              <div className="ld-tabs">
                <button
                  type="button"
                  className={`ld-tab${
                    activeTab === "monthly" ? " active" : ""
                  }`}
                  onClick={() => setActiveTab("monthly")}
                >
                  📅 Monthly Summary
                </button>

                <button
                  type="button"
                  className={`ld-tab${
                    activeTab === "transactions" ? " active" : ""
                  }`}
                  onClick={() => setActiveTab("transactions")}
                >
                  🧾 Transactions ({transactions.length})
                </button>
              </div>

              {transactionLoading ? (
                <Loader />
              ) : activeTab === "monthly" ? (
                <MonthlySummary data={monthlySummary} />
              ) : (
                <TransactionHistory data={transactions} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// =====================================================
// MONTHLY SUMMARY
// =====================================================

function MonthlySummary({ data }) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="ld-empty">
        No monthly data available for this year.
      </div>
    );
  }

  return (
    <div className="month-pill-grid">
      {data.map((month, index) => (
        <div
          key={`${month.month ?? "month"}-${index}`}
          className="month-pill"
        >
          <div className="month-pill-name">
            {monthLabel(month.month)}
          </div>

          <div className="month-pill-row">
            <span className="mp-label">PL Used</span>
            <span className="mp-val c-red">
              {fmt(month.planned_used)}
            </span>
          </div>

          <div className="month-pill-row">
            <span className="mp-label">EL Used</span>
            <span className="mp-val c-purple">
              {fmt(month.unplanned_used)}
            </span>
          </div>

          <div className="month-pill-row">
            <span className="mp-label">Earned</span>
            <span className="mp-val c-green">
              {fmt(month.credits)}
            </span>
          </div>

          <div className="month-pill-row">
            <span className="mp-label">LOP</span>
            <span className="mp-val c-amber">
              {fmt(month.loss_of_pay)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// =====================================================
// TRANSACTION HISTORY
// =====================================================

function TransactionHistory({ data }) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="ld-empty">
        No transactions found.
      </div>
    );
  }

  return (
    <>
      {/* DESKTOP TABLE */}
      <div className="ld-table-wrap txn-desktop-table">
        <table className="ld-table">
          <thead>
            <tr>
              <th>Date &amp; Time (IST)</th>
              <th>Leave Type</th>
              <th>Transaction</th>
              <th>Balance Change</th>
              <th>Remarks</th>
            </tr>
          </thead>

          <tbody>
            {data.map((txn) => {
              const badgeClass = getBadgeClass(
                txn.transaction_type || ""
              );

              const totalDays = Number(txn.days || 0);
              const deductedDays = Number(txn.deducted_days || 0);
              const lopDays = Number(txn.lop_days || 0);

              const balBefore = Number(txn.balance_before || 0);
              const balAfter = Number(txn.balance_after || 0);

              const balDiff = balAfter - balBefore;
              const isPositive = balDiff >= 0;

              const istDate = toIST(txn.created_at);
              const validDate = !Number.isNaN(istDate.getTime());

              return (
                <tr key={txn.id}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {validDate ? (
                      <>
                        <div className="txn-main">
                          {istDate.toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>

                        <div className="txn-sub">
                          {istDate.toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}{" "}
                          IST
                        </div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td style={{ whiteSpace: "nowrap" }}>
                    <div className="txn-main">
                      {txn.leave_type_name || "—"}
                    </div>

                    <div
                      className="detail-block"
                      style={{ marginTop: 4 }}
                    >
                      <div className="detail-row">
                        <span className="detail-label">
                          Total
                        </span>

                        <span className="detail-val">
                          {fmt(totalDays)}{" "}
                          <span
                            style={{
                              color: "var(--muted)",
                              fontSize: 11,
                            }}
                          >
                            day{totalDays !== 1 ? "s" : ""}
                          </span>
                        </span>
                      </div>

                      {deductedDays > 0 && (
                        <div className="detail-row">
                          <span className="detail-label">
                            Deducted
                          </span>

                          <span className="detail-val c-red">
                            −{fmt(deductedDays)}
                          </span>
                        </div>
                      )}

                      {lopDays > 0 && (
                        <div className="detail-row">
                          <span className="detail-label">
                            LOP
                          </span>

                          <span className="detail-val c-amber">
                            {fmt(lopDays)}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <span className={`ld-badge ${badgeClass}`}>
                      {txn.transaction_label ||
                        txn.transaction_type ||
                        "—"}
                    </span>

                    {txn.transaction_type && (
                      <div style={{ marginTop: 4 }}>
                        <span className="txn-chip">
                          {txn.transaction_type}
                        </span>
                      </div>
                    )}
                  </td>

                  <td style={{ whiteSpace: "nowrap" }}>
                    <div className="bal-block">
                      <div className="bal-row">
                        <span className="bal-label">
                          Before
                        </span>

                        <span className="bal-val c-muted">
                          {fmt(balBefore)}
                        </span>
                      </div>

                      <div
                        className="bal-row"
                        style={{ margin: "2px 0" }}
                      >
                        <span className="bal-label" />

                        <span
                          className="bal-diff"
                          style={{
                            color: isPositive
                              ? "var(--green)"
                              : "var(--red)",
                          }}
                        >
                          {isPositive ? "▲ +" : "▼ "}
                          {fmt(Math.abs(balDiff))}
                        </span>
                      </div>

                      <div className="bal-row">
                        <span className="bal-label">
                          After
                        </span>

                        <span
                          className="bal-val"
                          style={{
                            color: isPositive
                              ? "var(--green)"
                              : "var(--red)",
                          }}
                        >
                          {fmt(balAfter)}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="remarks-cell">
                      {txn.split_summary ? (
                        <>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 11,
                              color: "var(--muted)",
                              marginBottom: 2,
                            }}
                          >
                            SPLIT SUMMARY
                          </div>

                          {txn.split_summary}
                        </>
                      ) : txn.remarks ? (
                        txn.remarks
                      ) : (
                        <span style={{ color: "var(--muted)" }}>
                          —
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="txn-mobile-list">
        {data.map((txn) => {
          const badgeClass = getBadgeClass(
            txn.transaction_type || ""
          );

          const balBefore = Number(
            txn.balance_before || 0
          );

          const balAfter = Number(
            txn.balance_after || 0
          );

          const balDiff = balAfter - balBefore;
          const isPositive = balDiff >= 0;

          const istDate = toIST(txn.created_at);
          const validDate = !Number.isNaN(istDate.getTime());

          const totalDays = Number(txn.days || 0);
          const lopDays = Number(txn.lop_days || 0);

          return (
            <div
              key={txn.id}
              className="txn-mobile-card"
            >
              <div className="txn-mobile-top">
                <div>
                  <div className="txn-main">
                    {txn.leave_type_name || "—"}
                  </div>

                  <div className="txn-sub">
                    {validDate
                      ? `${istDate.toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }
                        )} · ${istDate.toLocaleTimeString(
                          "en-IN",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )} IST`
                      : "—"}
                  </div>

                  {txn.transaction_type && (
                    <span
                      className="txn-chip"
                      style={{ marginTop: 4 }}
                    >
                      {txn.transaction_type}
                    </span>
                  )}
                </div>

                <span
                  className={`ld-badge ${badgeClass}`}
                >
                  {txn.transaction_label ||
                    txn.transaction_type ||
                    "—"}
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(3, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <div>
                  <div className="emp-ms-label">
                    Days
                  </div>

                  <div className="emp-ms-val">
                    {fmt(totalDays)}
                  </div>
                </div>

                <div>
                  <div className="emp-ms-label">
                    Before
                  </div>

                  <div className="emp-ms-val c-muted">
                    {fmt(balBefore)}
                  </div>
                </div>

                <div>
                  <div className="emp-ms-label">
                    Change
                  </div>

                  <div
                    className="emp-ms-val"
                    style={{
                      color: isPositive
                        ? "var(--green)"
                        : "var(--red)",
                    }}
                  >
                    {isPositive ? "+" : ""}
                    {fmt(balDiff)}
                  </div>
                </div>

                <div>
                  <div className="emp-ms-label">
                    After
                  </div>

                  <div
                    className="emp-ms-val"
                    style={{
                      color: isPositive
                        ? "var(--green)"
                        : "var(--red)",
                    }}
                  >
                    {fmt(balAfter)}
                  </div>
                </div>

                {lopDays > 0 && (
                  <div>
                    <div className="emp-ms-label">
                      LOP
                    </div>

                    <div className="emp-ms-val c-amber">
                      {fmt(lopDays)}
                    </div>
                  </div>
                )}
              </div>

              {(txn.remarks || txn.split_summary) && (
                <div className="txn-mobile-footer">
                  {txn.split_summary ? (
                    <>
                      <strong
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                        }}
                      >
                        SPLIT:{" "}
                      </strong>
                      {txn.split_summary}
                    </>
                  ) : (
                    txn.remarks
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// =====================================================
// SMALL COMPONENTS
// =====================================================

function MobileStat({ label, value, color }) {
  return (
    <div>
      <div className="emp-ms-label">
        {label}
      </div>

      <div
        className="emp-ms-val"
        style={color ? { color } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

function Loader() {
  return (
    <div className="ld-loading">
      <div className="ld-spinner" />
      Loading…
    </div>
  );
}
